import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Building, Users, Mail, Home, ArrowRight, FileText, Tag,
  CheckCircle, Clock, XCircle, Bell, Plus,
  Eye, MessageSquare, MapPin, BarChart2, Activity,
  Settings, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useTranslation } from '../../contexts/LanguageContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new:       { label: 'Ново',      color: 'bg-blue-100 text-blue-800',    dot: 'bg-blue-500' },
  assigned:  { label: 'Назначено', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  completed: { label: 'Завършено', color: 'bg-green-100 text-green-800',   dot: 'bg-green-500' },
  cancelled: { label: 'Отказано',  color: 'bg-red-100 text-red-800',       dot: 'bg-red-500' },
};

const PIE_COLORS = ['#1B5E37', '#4CAF50', '#FFC107', '#F44336'];

function StatCard({ label, value, icon: Icon, color, sub, link }) {
  const card = (
    <div className="admin-card group hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center shadow-sm`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {link && <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />}
      </div>
      <p className="text-3xl font-bold text-foreground mb-1">{value}</p>
      <p className="text-sm font-medium text-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
  return link ? <Link to={link}>{card}</Link> : card;
}

function InquiryRow({ inquiry }) {
  const status = STATUS_CONFIG[inquiry.status] || STATUS_CONFIG.new;
  const diff = Date.now() - new Date(inquiry.created_at).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const timeAgo = days > 0 ? `преди ${days} дни` : hours > 0 ? `преди ${hours} ч.` : `преди ${mins} мин.`;

  return (
    <div className="flex items-center gap-3 py-3 border-b border-border last:border-0 hover:bg-secondary/30 px-2 rounded-lg transition-colors">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-medium text-sm truncate">{inquiry.name}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">{timeAgo}</span>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {inquiry.message ? inquiry.message.slice(0, 55) + '...' : inquiry.email}
        </p>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${status.color}`}>
        {status.label}
      </span>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user, isBroker } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ properties: 0, apartments: 0, contacts: 0, inquiries: 0, listings: 0, newInquiries: 0 });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [properties, setProperties] = useState([]);
  const [inquiryByStatus, setInquiryByStatus] = useState([]);
  const [inquiryByMonth, setInquiryByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [propRes, aptRes, contactsRes, inquiriesRes, listingsRes] = await Promise.allSettled([
        axios.get(`${API}/properties`),
        axios.get(`${API}/apartments`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/listings`),
      ]);

      const props = propRes.status === 'fulfilled' ? (propRes.value.data || []) : [];
      const apts = aptRes.status === 'fulfilled' ? (aptRes.value.data || []) : [];
      const contacts = contactsRes.status === 'fulfilled' ? (contactsRes.value.data || []) : [];
      const inquiries = inquiriesRes.status === 'fulfilled' ? (inquiriesRes.value.data || []) : [];
      const listings = listingsRes.status === 'fulfilled' ? (listingsRes.value.data || []) : [];
      const newCount = inquiries.filter(i => i.status === 'new').length;

      // Pie data
      const statusCounts = {};
      inquiries.forEach(i => { statusCounts[i.status] = (statusCounts[i.status] || 0) + 1; });
      const pieData = Object.entries(statusCounts).map(([s, c]) => ({
        name: STATUS_CONFIG[s]?.label || s, value: c
      }));

      // Bar data - last 6 months
      const monthData = {};
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthData[d.toLocaleDateString('bg-BG', { month: 'short' })] = 0;
      }
      inquiries.forEach(inq => {
        const key = new Date(inq.created_at).toLocaleDateString('bg-BG', { month: 'short' });
        if (key in monthData) monthData[key]++;
      });

      setStats({ properties: props.length, apartments: apts.length,
        contacts: contacts.length, inquiries: inquiries.length,
        listings: listings.length, newInquiries: newCount });
      setRecentInquiries([...inquiries].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6));
      setProperties(props.slice(0, 4));
      setInquiryByStatus(pieData);
      setInquiryByMonth(Object.entries(monthData).map(([month, count]) => ({ month, count })));
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setLoading(false);
    }
  };

  const h = time.getHours();
  const greeting = h < 12 ? t('dashboard.morning') : h < 18 ? t('dashboard.afternoon') : t('dashboard.evening');

  // Broker dashboard - simple redirect to listings
  if (isBroker) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-[#1B5E37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Tag className="w-10 h-10 text-[#1B5E37]" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">{t('dashboard.welcome')}, {user?.name}!</h1>
          <p className="text-muted-foreground mb-6">{t('dashboard.manage_listings')}</p>
          <Button onClick={() => navigate('/admin/listings')} className="bg-[#1B5E37] hover:bg-[#154a2c]" size="lg">
            <Tag className="w-5 h-5 mr-2" /> Моите обяви
          </Button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="admin-dashboard" className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">{greeting} 👋</h1>
          <p className="text-muted-foreground text-sm mt-1 flex items-center gap-2 flex-wrap">
            {time.toLocaleDateString('bg-BG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {stats.newInquiries > 0 && (
              <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
                <Bell className="w-3 h-3" /> {stats.newInquiries} нови запитвания
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/properties">
            <Button size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" />{t('props.new_project')}</Button>
          </Link>
          <Link to="/admin/inquiries">
            <Button size="sm" className="bg-[#1B5E37] hover:bg-[#154a2c] relative">
              <Mail className="w-4 h-4 mr-1" />Запитвания
              {stats.newInquiries > 0 && (
                <span className="ml-1.5 bg-white text-[#1B5E37] text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {stats.newInquiries}
                </span>
              )}
            </Button>
          </Link>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t('admin.projects')} value={stats.properties} icon={Building}
          color="bg-[#1B5E37]" sub={t('dashboard.residential_complexes')} link="/admin/properties" />
        <StatCard label={t('dashboard.apartments')} value={stats.apartments} icon={Home}
          color="bg-blue-500" sub={t('dashboard.in_all_projects')} link="/admin/properties" />
        <StatCard label={t('admin.listings_nav')} value={stats.listings} icon={FileText}
          color="bg-purple-500" sub={t('dashboard.sale_and_rent')} link="/admin/listings" />
        <StatCard label={t("admin.inquiries_nav")} value={stats.inquiries} icon={MessageSquare}
          color="bg-orange-500" sub={stats.newInquiries > 0 ? `${stats.newInquiries} нови` : 'общо'} link="/admin/inquiries" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 admin-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-foreground flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-[#1B5E37]" /> Запитвания по месеци
            </h2>
            <span className="text-xs text-muted-foreground">{t('dashboard.last_6months')}</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={inquiryByMonth} barSize={32}>
              <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={25} />
              <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                formatter={(v) => [v, 'Запитвания']} />
              <Bar dataKey="count" fill="#1B5E37" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-card">
          <h2 className="font-heading font-semibold text-foreground flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-[#1B5E37]" /> По статус
          </h2>
          {inquiryByStatus.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={inquiryByStatus} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {inquiryByStatus.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t('admin.no_data')}</div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Recent Inquiries */}
        <div className="lg:col-span-2 admin-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-foreground flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1B5E37]" /> Последни запитвания
            </h2>
            <Link to="/admin/inquiries">
              <Button variant="ghost" size="sm" className="text-xs">Виж всички <ArrowRight className="w-3 h-3 ml-1" /></Button>
            </Link>
          </div>
          {recentInquiries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Няма запитвания все още</p>
            </div>
          ) : (
            recentInquiries.map(inq => <InquiryRow key={inq.id} inquiry={inq} />)
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">

          {/* Projects */}
          <div className="admin-card">
            <h2 className="font-heading font-semibold text-foreground flex items-center gap-2 mb-4">
              <Building className="w-5 h-5 text-[#1B5E37]" /> Проекти
            </h2>
            {properties.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Няма проекти</p>
            ) : (
              <div className="space-y-2">
                {properties.map(p => (
                  <Link key={p.id} to={`/properties/${p.id}`} target="_blank"
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors group">
                    <img src={p.main_image || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=80'}
                      alt={p.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 flex-shrink-0" />{p.location}
                      </p>
                    </div>
                    <Eye className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="admin-card">
            <h2 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-[#1B5E37]" /> Бързи действия
            </h2>
            <div className="space-y-1">
              {[
                { to: '/admin/properties', icon: Building, label: t('props.new_project'), sub: t('props.cat.residential') },
                { to: '/admin/listings',   icon: FileText,  label: t('listings.new_listing'),  sub: t('listings.sale_or_rent') },
                { to: '/admin/about',      icon: Users,     label: 'Екип & Партньори', sub: 'Управлявай За нас' },
                { to: '/admin/settings',   icon: Settings,  label: 'Настройки',   sub: 'Слайдшоу, карта, контакти' },
              ].map(({ to, icon: Icon, label, sub }) => (
                <Link key={to} to={to}
                  className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary transition-colors group">
                  <div className="w-8 h-8 bg-[#1B5E37]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1B5E37]/20 transition-colors flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#1B5E37]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{sub}</p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
